import { Box, useTheme } from "@mui/material";
import { CircleMinus } from "lucide-react";
import { BoardStepUiStatus } from "../dtos";

/**
 * The status glyph shown on step cards and jump lists (design: green check / red x /
 * dashed accent circle for in-progress / dashed grey for upcoming).
 *
 * The IN_PROGRESS ring is the design's exact asset ("CRM Web Delivery Loading.svg",
 * 2026-08-05): an 18×18 circle, #E6EFF5 fill, #015C9A stroke, stroke-dasharray 4 4 —
 * a CSS dashed border draws different dash geometry, so the SVG path is used verbatim.
 */
const InProgressRing = ({ size }: { size: number }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 18 18"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
    style={{ flexShrink: 0 }}
  >
    <path
      d="M10.7559 0.171875C11.9368 0.405429 13.0343 0.870066 14 1.5166L13.7227 1.93164C14.6496 2.55226 15.4477 3.35042 16.0684 4.27734L16.4824 3.99902C17.1291 4.96473 17.5934 6.06223 17.8271 7.24316L17.3379 7.34082C17.444 7.87706 17.5 8.43175 17.5 9C17.5 9.56825 17.444 10.1229 17.3379 10.6592L17.8271 10.7559C17.5935 11.9369 17.1291 13.0342 16.4824 14L16.0684 13.7227C15.4477 14.6496 14.6496 15.4477 13.7227 16.0684L14 16.4824C13.0342 17.1291 11.9369 17.5935 10.7559 17.8271L10.6592 17.3379C10.1229 17.444 9.56825 17.5 9 17.5C8.43175 17.5 7.87706 17.444 7.34082 17.3379L7.24316 17.8271C6.06223 17.5934 4.96473 17.1291 3.99902 16.4824L4.27734 16.0684C3.35042 15.4477 2.55226 14.6496 1.93164 13.7227L1.5166 14C0.870066 13.0343 0.405429 11.9368 0.171875 10.7559L0.662109 10.6592C0.556036 10.1229 0.5 9.56825 0.5 9C0.5 8.43175 0.556036 7.87706 0.662109 7.34082L0.171875 7.24316C0.405544 6.06234 0.870002 4.96467 1.5166 3.99902L1.93164 4.27734C2.55226 3.35042 3.35042 2.55226 4.27734 1.93164L3.99902 1.5166C4.96467 0.870002 6.06234 0.405544 7.24316 0.171875L7.34082 0.662109C7.87706 0.556036 8.43175 0.5 9 0.5C9.56825 0.5 10.1229 0.556036 10.6592 0.662109L10.7559 0.171875Z"
      fill="#E6EFF5"
      stroke="#015C9A"
      strokeDasharray="4 4"
    />
  </svg>
);

/** Design asset "attention-filled" (delivered 2026-08-05): filled circle + exclamation. Red
 * #DA3E33 = "Edits needed" (rejected feedback) and "Rejected"; orange #F76700 = "Conditional"
 * (same treatment, softer verdict — approved with conditions); yellow #A8A823 = "Ready".
 * The fill always matches the ramp of the status badge sitting beside it on the same row. */
const AttentionFilled = ({ size, fill = "#DA3E33" }: { size: number; fill?: string }) => (
  <svg width={size} height={size} viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path
      d="M9 0.75C13.5563 0.75 17.25 4.44365 17.25 9C17.25 13.5563 13.5563 17.25 9 17.25C4.44365 17.25 0.75 13.5563 0.75 9C0.75 4.44365 4.44365 0.75 9 0.75ZM9 12C8.58579 12 8.25 12.3358 8.25 12.75V13.125C8.25 13.5392 8.58579 13.875 9 13.875C9.41421 13.875 9.75 13.5392 9.75 13.125V12.75C9.75 12.3358 9.41421 12 9 12ZM9 4.5C8.58579 4.5 8.25 4.83579 8.25 5.25V10.125C8.25 10.5392 8.58579 10.875 9 10.875C9.41421 10.875 9.75 10.5392 9.75 10.125V5.25C9.75 4.83579 9.41421 4.5 9 4.5Z"
      fill={fill}
    />
  </svg>
);

/** Design asset "Subtract" (delivered 2026-08-05): green #3E8D7B filled circle-check — the DONE glyph. */
const CheckFilled = ({ size }: { size: number }) => (
  <svg width={size} height={size} viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ flexShrink: 0 }}>
    <path
      fillRule="evenodd"
      clipRule="evenodd"
      d="M8.25 0C12.8063 0 16.5 3.69365 16.5 8.25C16.5 12.8063 12.8063 16.5 8.25 16.5C3.69365 16.5 0 12.8063 0 8.25C0 3.69365 3.69365 0 8.25 0ZM12.4885 4.68091C12.1744 4.41123 11.7008 4.44743 11.4309 4.76147L6.79834 10.1528L5.02588 8.4082C4.73072 8.11759 4.25594 8.12111 3.96533 8.41626C3.67492 8.71143 3.67903 9.18626 3.97412 9.47681L6.31787 11.7847C6.46587 11.9302 6.66783 12.0079 6.87524 11.9993C7.08268 11.9905 7.27751 11.896 7.41284 11.7385L12.5691 5.73853C12.8388 5.42438 12.8026 4.95079 12.4885 4.68091Z"
      fill="#3E8D7B"
    />
  </svg>
);

const StepStatusIcon = ({
  status,
  size = 18,
}: {
  status: BoardStepUiStatus;
  size?: number;
}) => {
  const theme = useTheme();
  const colors = theme.nawy.colors;

  const circle = (border: string, backgroundColor?: string) => (
    <Box
      sx={{
        width: size,
        height: size,
        flexShrink: 0,
        borderRadius: "50%",
        border,
        backgroundColor,
      }}
    />
  );

  switch (status) {
    case "DONE":
      return <CheckFilled size={size} />;
    case "EDITS_NEEDED":
      return <AttentionFilled size={size} />;
    case "CONDITIONAL":
      return <AttentionFilled size={size} fill="#F76700" />;
    case "BLOCKER":
      // §MobileQA: every [Mobile] rejected frame (dev-compound "Rejected", seller & unit
      // "Missing data") draws the SOLID #DA3E33 attention disc — sampled 18×18, no outline
      // anywhere. A lucide CircleX outline in red[700] read lighter than the design and than
      // the EDITS_NEEDED row right above it, which is the same red state one notch softer.
      return <AttentionFilled size={size} />;
    case "SKIPPED":
      // not-applicable: grey minus — deliberately NOT the green check, no work happened here
      return <CircleMinus size={size} color={colors.semantic.text.tertiary} />;
    case "READY":
      // §W3.4: form filled, awaiting submit. The design ([Mobile] Credit documents - Filled)
      // uses the SAME filled attention disc on the yellow indicator ramp (#A8A823, sampled),
      // so the glyph agrees with the pale-yellow "Ready" badge beside it. The lucide outline
      // in orange[700] belonged to no ramp on the row.
      return <AttentionFilled size={size} fill="#A8A823" />;
    case "IN_PROGRESS":
      return <InProgressRing size={size} />;
    case "UPCOMING":
      return circle(`1px dashed ${colors.semantic.border.primary}`);
    case "TODO":
    default:
      return circle(`1px solid ${colors.semantic.border.primary}`);
  }
};

export default StepStatusIcon;
